"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  buildLiteratureReviewOutline,
  type LiteratureReviewInput,
  ORGANIZATIONS,
  type Organization,
} from "@/lib/tool-kit/research-writing/literature-review-outline";

/** English strings. A translated content file overrides these through `ui`. */
export const LITERATURE_REVIEW_OUTLINE_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  reviewHeading: "Your review",
  titleLabel: "Working title",
  titlePlaceholder:
    "e.g. Peer Tutoring and Student Retention in Higher Education",
  wordsLabel: "Target length (words, optional)",
  wordsPlaceholder: "e.g. 3000",
  wordsHint:
    "Adds a word target to each section: 10% introduction, 75% body split evenly, 15% conclusion.",
  questionLabel: "The question or purpose of the review",
  questionPlaceholder:
    "What do you want the literature to tell you, or what argument does it support?",
  scopeLabel: "How you searched and chose sources (optional)",
  scopePlaceholder: "Databases, years, inclusion and exclusion criteria",
  gapLabel: "The gap you are heading for (optional)",
  gapPlaceholder: "What the literature has not done, or has done only in part",
  organizationLabel: "How will the body be organized?",
  organizations: {
    thematic: "By theme",
    chronological: "By time",
    methodological: "By method",
  },
  organizationHints: {
    thematic:
      "Around ideas, not sources. The most common choice in the social and behavioral sciences.",
    chronological:
      "Only if the order shows a real trend, such as research building on earlier research.",
    methodological:
      "Around how studies were done: surveys, experiments, interviews, and so on.",
  },
  sectionsLabels: {
    thematic: "Themes — one per line",
    chronological: "Periods or stages — one per line",
    methodological: "Methods or approaches — one per line",
  },
  sectionsPlaceholders: {
    thematic:
      "Academic outcomes: Smith 2019; Lee 2020\nFirst-generation students",
    chronological:
      "Before 2000: early evaluations\n2000–2015: large-scale trials",
    methodological: "Randomized trials: …\nInterviews and case studies",
  },
  sectionsHint:
    "Optionally add sources or notes after a colon. Leave this empty to get three numbered placeholder sections.",
  fileName: "literature-review-outline",
  document: {
    heading: "Your literature review outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part has your content.",
  },
  defaultTitle: "Literature Review",
  subtitle: "{organization} literature review outline",
  subtitleWords: "{organization} literature review outline · {words} words",
  organizationNames: {
    thematic: "Thematic",
    chronological: "Chronological",
    methodological: "Methodological",
  },
  headings: {
    intro: "Introduction",
    conclusion: "Conclusion",
    budget: "Word Budget",
  },
  sectionHeading: "{n}. {name}",
  sectionNames: {
    thematic: "Theme {n}",
    chronological: "Period {n}",
    methodological: "Method {n}",
  },
  target: "Target: about {words} words.",
  tips: {
    intro:
      "Give the background, say what the review is for and how you searched, and tell the reader how it is organized.",
    conclusion:
      "Summarize what the literature shows, say what is missing, and point to what comes next. A review adds no new study of its own.",
    thematic:
      "A theme is an idea, not a source. Chronology can still appear inside a theme.",
    chronological:
      "Use time as the frame only if the order shows a trend. Otherwise it reads as a list of dates.",
    methodological:
      "Organize by how studies were done, not by what they found, and compare what each method can and cannot show.",
  },
  prompts: {
    background:
      "Give the background: what the topic is and why it matters, in two or three sentences.",
    question: "State the question or purpose the review serves.",
    scope:
      "Say how you searched and chose sources: databases, years, inclusion criteria.",
    summary:
      "Summarize what the literature has established, and where studies disagree.",
    gap: "State the gap: “However, [what is missing], which is why [your study or question].”",
    next: "Say what comes next: your research question, method or argument.",
  },
  organizingPrompts: {
    thematic:
      "Tell the reader the review is organized by theme, and name the themes in order.",
    chronological:
      "Tell the reader the review follows the field over time, and name the periods.",
    methodological:
      "Tell the reader the review is organized by method, and name the methods.",
  },
  topicSentencePrompts: {
    thematic:
      "Open with a claim about this theme: “Research on [theme] shows [point], although [qualification].”",
    chronological:
      "Open with what defined this period: “In [period], researchers asked [question] and found [point].”",
    methodological:
      "Open with the method: “Studies using [method] show [point], within the limits of [limit].”",
  },
  notesLead: "Sources and notes:",
  questionsHeading: "Synthesis questions to answer",
  questionsThematic: [
    "State the point of this theme in one sentence of your own.",
    "Group the studies that support it. Where do they agree?",
    "Where do they disagree, and is the difference explained by samples, methods or definitions?",
    "Which evidence is strongest, and why? Evaluate it rather than only reporting it.",
    "What does this theme leave unanswered?",
  ],
  questionsChronological: [
    "What questions, methods or findings defined this period?",
    "What changed from the previous period, and what prompted the change?",
    "Which studies set the direction, and which were later challenged?",
    "What carried forward and what was abandoned?",
    "Does the order show a real trend, or only a sequence of dates?",
  ],
  questionsMethodological: [
    "Which studies use this method, and what did they find?",
    "What can this method show, and what can it not show?",
    "How do its findings differ from those of the other methods in this review?",
    "What are its usual weaknesses in this field: samples, measures, designs?",
    "What would a different method add?",
  ],
  closingQuestions: [
    "Link: how does this section lead to the next, or back to your question?",
    "Cite every claim, and keep your own voice at the start and end of each paragraph.",
  ],
  budgetHead: { section: "Section", words: "Target words" },
  budgetTotal: "Total",
  example: {
    title: "Peer Tutoring and Student Retention in Higher Education",
    words: "3000",
    question:
      "What does existing research show about whether peer tutoring helps students stay enrolled, and for whom?",
    scope:
      "Databases: [list]. Years: [range]. Included: [criteria]. Excluded: [criteria].",
    gap: "However, few studies follow tutored students into their second year, and fewer report results for first-generation students separately, which is why this project asks whether tutoring predicts re-enrollment.",
    sections:
      "Academic outcomes: [sources on grades and pass rates]\nPersistence and re-enrollment: [sources on staying enrolled]\nFirst-generation and underrepresented students: [sources]\nHow tutoring is delivered: [sources on training, format and dosage]",
  },
} as const;

const EMPTY: LiteratureReviewInput = {
  title: "",
  question: "",
  scope: "",
  gap: "",
  organization: "thematic",
  sections: "",
  words: "",
};

type TextKey = Exclude<keyof LiteratureReviewInput, "organization">;

export default function LiteratureReviewOutlineGeneratorWidget() {
  const strings = useToolUi(LITERATURE_REVIEW_OUTLINE_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<LiteratureReviewInput>(EMPTY);
  const labels = useMemo(
    () => ({
      ...strings,
      organizations: strings.organizationNames,
      prompts: {
        ...strings.prompts,
        organizing: strings.organizingPrompts,
        topicSentence: strings.topicSentencePrompts,
      },
      questions: {
        thematic: strings.questionsThematic,
        chronological: strings.questionsChronological,
        methodological: strings.questionsMethodological,
      },
    }),
    [strings]
  );
  const doc = useMemo(
    () => buildLiteratureReviewOutline(input, labels, fmt),
    [input, labels, fmt]
  );
  const documentLabels = useMemo(() => strings.document, [strings]);
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const organization = input.organization;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput({ ...EMPTY, ...strings.example, organization: "thematic" })
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.reviewHeading}>
        <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
          <TextField
            id="lr-title"
            label={strings.titleLabel}
            onChange={update("title")}
            placeholder={strings.titlePlaceholder}
            value={input.title}
          />
          <TextField
            hint={strings.wordsHint}
            id="lr-words"
            label={strings.wordsLabel}
            numeric
            onChange={update("words")}
            placeholder={strings.wordsPlaceholder}
            value={input.words}
          />
        </div>
        <TextAreaField
          id="lr-question"
          label={strings.questionLabel}
          onChange={update("question")}
          placeholder={strings.questionPlaceholder}
          prose
          rows={2}
          value={input.question}
        />
        <TextAreaField
          id="lr-scope"
          label={strings.scopeLabel}
          onChange={update("scope")}
          placeholder={strings.scopePlaceholder}
          prose
          rows={2}
          value={input.scope}
        />
        <TextAreaField
          id="lr-gap"
          label={strings.gapLabel}
          onChange={update("gap")}
          placeholder={strings.gapPlaceholder}
          prose
          rows={2}
          value={input.gap}
        />
      </Panel>

      <Panel title={strings.organizationLabel}>
        <PillGroup
          label={strings.organizationLabel}
          name="lr-organization"
          onChange={(value: Organization) =>
            setInput((previous) => ({ ...previous, organization: value }))
          }
          options={ORGANIZATIONS.map((value) => ({
            value,
            label: strings.organizations[value],
          }))}
          value={organization}
        />
        <p className="text-muted-foreground text-xs">
          {strings.organizationHints[organization]}
        </p>
        <TextAreaField
          hint={strings.sectionsHint}
          id="lr-sections"
          label={strings.sectionsLabels[organization]}
          onChange={update("sections")}
          placeholder={strings.sectionsPlaceholders[organization]}
          prose
          rows={5}
          value={input.sections}
        />
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="lr"
        labels={documentLabels}
      />
    </div>
  );
}
